import { useEffect } from 'react';

// Input types where Enter should NOT trigger form submit
const SKIP_TYPES = new Set([
  'checkbox', 'radio', 'file', 'button', 'submit', 'reset',
  'image', 'range', 'color',
]);

// CSS selectors for containers that should NOT trigger submit on Enter
// (search inputs inside data grids, header search bars, etc.)
const SKIP_CONTAINERS = [
  '.MuiAutocomplete-root',   // autocomplete / suggestion dropdowns
  '.MuiDataGrid-root',       // table cell editors
  '.MuiTablePagination-root',
];

function shouldSkip(el) {
  return SKIP_CONTAINERS.some((sel) => el.closest(sel));
}

/**
 * Global Enter-to-submit hook.
 *
 * Behaviour:
 *  1. If a MUI Dialog is open → Enter clicks the primary (contained, enabled)
 *     action button of the topmost dialog.
 *  2. Otherwise → walks up the DOM from the focused input (max 20 levels) and
 *     clicks the first primary contained button found in that subtree.
 *
 * Skipped cases:
 *  - Shift/Ctrl/Meta/Alt + Enter
 *  - Focused element is not a plain <input>
 *  - Input type is non-text (checkbox, radio, file …)
 *  - Input is inside an Autocomplete or DataGrid
 *  - Input is read-only
 */
export default function useEnterToSubmit() {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (
        e.key !== 'Enter' ||
        e.shiftKey ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) return;

      const active = document.activeElement;
      if (!active) return;

      // Only act from plain text inputs
      if (active.tagName.toLowerCase() !== 'input') return;
      if (SKIP_TYPES.has(active.type)) return;
      if (active.readOnly) return;
      if (shouldSkip(active)) return;

      // ── 1. MUI Dialog open? ──────────────────────────────────────────────
      const dialogs = document.querySelectorAll('.MuiDialog-paper');
      if (dialogs.length > 0) {
        // Topmost dialog (last in DOM = highest z-index)
        const topDialog = dialogs[dialogs.length - 1];
        const actions = topDialog.querySelector('.MuiDialogActions-root');
        if (actions) {
          // Primary button: contained variant that is enabled
          const primary = actions.querySelector(
            'button.MuiButton-contained:not([disabled])'
          );
          if (primary) {
            e.preventDefault();
            primary.click();
          }
        }
        return; // don't fall through to page-level handler
      }

      // ── 2. No dialog — walk up to find the nearest primary button ────────
      let el = active.parentElement;
      let depth = 0;
      while (el && el !== document.body && depth < 20) {
        const primary = el.querySelector(
          'button.MuiButton-contained:not([disabled])'
        );
        if (primary) {
          e.preventDefault();
          primary.click();
          return;
        }
        el = el.parentElement;
        depth++;
      }
    };

    // Use capture phase so this runs before child keydown handlers
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
}
